import { readCookie } from './cookies.js';

/**
 * Consent status is recorded on every event and deliberately never enforced. Spec §3
 * cookie_blocked needs it to tell "no traffic" apart from "traffic we were not allowed to
 * measure", which only works if the event still arrives.
 *
 * There used to be a `consent_gating` setting here. It was wired to the settings screen and
 * to the browser context but read by nothing, so switching it on changed no behaviour. A
 * compliance control that silently does nothing is worse than none, so it was removed
 * rather than half-implemented. If gating is ever wanted, do it in Tracker.track() and keep
 * the §3 diagnostics ungated.
 */

export const GRANTED = 'granted';
export const DENIED = 'denied';
export const UNKNOWN = 'unknown';

/**
 * Reads whichever consent signal the site happens to expose. Order matters: an
 * explicit CMP answer beats an inferred one.
 */
export function consentStatus(win = typeof window !== 'undefined' ? window : {}) {
  const doc = win.document;

  // Google Consent Mode, as the CMP documented it: a `consent` default/update push.
  const pushed = dataLayerConsent(win);
  if (pushed !== UNKNOWN) {
    return pushed;
  }

  // google_tag_data.ics is gtag's private state; only 1 (granted) is safe to read, the
  // encoding of everything else is undocumented and has changed before.
  const gcm = win.google_tag_data && win.google_tag_data.ics;
  if (gcm && typeof gcm.getConsentState === 'function') {
    try {
      if (gcm.getConsentState('analytics_storage') === 1) {
        return GRANTED;
      }
    } catch (e) {
      /* fall through */
    }
  }

  if (!doc) {
    return UNKNOWN;
  }

  // Complianz / CookieYes / Cookiebot cookies used across the NF stores.
  const cmpCookies = [
    ['cmplz_marketing', { allow: 'allow', deny: ['disallow', 'deny'] }],
    ['cmplz_statistics', { allow: 'allow', deny: ['disallow', 'deny'] }],
    ['cookieyes-consent', { allow: 'analytics:yes', deny: ['analytics:no'] }],
  ];

  for (let i = 0; i < cmpCookies.length; i++) {
    const [name, values] = cmpCookies[i];
    const raw = readCookie(name, doc);

    if (!raw) {
      continue;
    }

    // Denials first, and "disallow" is one of them: it *contains* "allow", so an
    // allow-first test reports a visitor who refused as granted. Reading consent wrongly
    // in that direction is the expensive one, so this fails closed. Kept in step with
    // EnvelopeFactory::consent_status() — see tests/php/Dispatch/ConsentParityTest.php.
    let denied = false;

    for (let d = 0; d < values.deny.length; d++) {
      if (raw.indexOf(values.deny[d]) !== -1) {
        denied = true;
      }
    }

    if (denied) {
      return DENIED;
    }

    if (raw.indexOf(values.allow) !== -1) {
      return GRANTED;
    }
  }

  return UNKNOWN;
}

/**
 * Last `gtag('consent', 'default'|'update', {...})` push for analytics_storage. The
 * dataLayer entries are `arguments` objects, so they are read positionally.
 */
function dataLayerConsent(win) {
  const layer = win.dataLayer;

  if (!layer || typeof layer.length !== 'number') {
    return UNKNOWN;
  }

  let status = UNKNOWN;

  for (let i = 0; i < layer.length; i++) {
    const entry = layer[i];

    if (!entry || entry[0] !== 'consent' || (entry[1] !== 'default' && entry[1] !== 'update')) {
      continue;
    }

    const value = entry[2] && entry[2].analytics_storage;

    if (value === GRANTED || value === DENIED) {
      status = value;
    }
  }

  return status;
}
